import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Image as ImageIcon,
  AlertCircle,
  Plus,
  Check,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";

const MAX_IMAGES = 4;

const FeaturedImagesUpload = ({
  value = [],
  onChange,
  maxSize = 5,
  acceptedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  className = "",
}) => {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState(value || []);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setImages(value);
    }
  }, [value]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    setError(null);

    if (!file.type.startsWith("image/")) {
      const errorMsg = "Please upload an image file";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (!acceptedFormats.includes(file.type)) {
      const errorMsg = `Accepted formats: ${acceptedFormats
        .map((f) => f.split("/")[1].toUpperCase())
        .join(", ")}`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (file.size > maxSize * 1024 * 1024) {
      const errorMsg = `Image size must be less than ${maxSize}MB`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const remainingSlots = MAX_IMAGES - images.length;

    if (fileArray.length > remainingSlots) {
      toast.warning(
        `You can only add ${remainingSlots} more image${
          remainingSlots === 1 ? "" : "s"
        }. Maximum ${MAX_IMAGES} images allowed.`
      );
      return;
    }

    const validFiles = fileArray.filter(validateFile);
    if (validFiles.length === 0) return;

    try {
      const newImages = await Promise.all(
        validFiles.map((file) => convertToBase64(file))
      );

      const updatedImages = [...images, ...newImages].slice(0, MAX_IMAGES);
      setImages(updatedImages);
      onChange(updatedImages);
      toast.success(
        `${newImages.length} image${
          newImages.length === 1 ? "" : "s"
        } added successfully`
      );
    } catch (error) {
      toast.error("Failed to upload images");
      setError("Failed to process images");
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    handleFileSelect(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onChange(updatedImages);
    toast.success("Image removed");
  };

  const handleReplaceImage = (index) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedFormats.join(",");
    input.multiple = false;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!validateFile(file)) return;

      try {
        const base64 = await convertToBase64(file);
        const updatedImages = [...images];
        updatedImages[index] = base64;
        setImages(updatedImages);
        onChange(updatedImages);
        toast.success("Image replaced successfully");
      } catch (error) {
        toast.error("Failed to replace image");
      }
    };
    input.click();
  };

  const canAddMore = images.length < MAX_IMAGES;

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <ImageIcon className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Featured Images</h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
            {images.length}/{MAX_IMAGES}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Upload up to {MAX_IMAGES} featured images for your event. These images
          will be displayed in the event gallery. Recommended size: 1920x1080px
          (16:9 aspect ratio)
        </p>

        {images.length > 0 && (
          <div className="grid grid-cols-2 gap-4 mb-4 sm:grid-cols-2 lg:grid-cols-4">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative group aspect-video overflow-hidden bg-gray-100 rounded-xl border-2 border-gray-200 shadow-md"
              >
                <img
                  src={image}
                  alt={`Featured image ${index + 1}`}
                  className="object-cover w-full h-full transition-transform group-hover:scale-110"
                />
                {/* Overlay on hover */}
                <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-40">
                  <div className="flex items-center space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => handleReplaceImage(index)}
                      className="p-2 text-white bg-blue-600 rounded-lg shadow-lg transition-colors hover:bg-blue-700"
                      title="Replace image"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="p-2 text-white bg-red-600 rounded-lg shadow-lg transition-colors hover:bg-red-700"
                      title="Remove image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Image number badge */}
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full shadow">
                    {index + 1}
                  </span>
                </div>
                {/* Success badge */}
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs text-white bg-green-600 rounded shadow">
                    <Check className="w-3 h-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add More Images Button or Upload Area */}
        {canAddMore && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
              isDragging
                ? "bg-purple-50 border-purple-500"
                : error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  error ? "bg-red-100" : "bg-purple-100"
                }`}
              >
                {error ? (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                ) : (
                  <Plus className="w-6 h-6 text-purple-600" />
                )}
              </div>
              <p
                className={`text-sm font-semibold mb-1 ${
                  error ? "text-red-900" : "text-gray-900"
                }`}
              >
                {isDragging
                  ? "Drop your images here"
                  : error
                  ? "Upload Failed"
                  : `Add Featured Image${images.length === 0 ? "s" : ""} (${
                      MAX_IMAGES - images.length
                    } remaining)`}
              </p>
              <p className="mb-2 text-xs text-gray-600">
                {error || "Drag and drop or click to browse"}
              </p>
              <p className="text-xs text-gray-500">
                {acceptedFormats
                  .map((f) => f.split("/")[1].toUpperCase())
                  .join(", ")}{" "}
                up to {maxSize}MB each
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        {images.length === 0 && (
          <div className="p-3 mt-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-purple-800">
                <p className="font-semibold">Recommended Specifications:</p>
                <ul className="mt-1 space-y-0.5 text-xs list-disc list-inside">
                  <li>Resolution: 1920x1080px (16:9 aspect ratio)</li>
                  <li>File size: Less than {maxSize}MB per image</li>
                  <li>Formats: JPG, PNG, or WEBP</li>
                  <li>High quality images work best</li>
                  <li>You can upload up to {MAX_IMAGES} images</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center mt-3 text-xs text-red-600">
            <AlertCircle className="mr-1 w-3 h-3" />
            {error}
          </div>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(",")}
          onChange={handleFileChange}
          multiple
          className="hidden"
        />
      </div>
    </div>
  );
};

export default FeaturedImagesUpload;
